import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser } from '../../shared/types';
import { queryClient } from '../../lib/queryClient';
import { apiRequest, getStoredToken, jsonBody, storeToken } from '../lib/api';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login(email: string, password: string): Promise<AuthUser>;
  employeeLogin(input: {
    branchId: string;
    role: 'reception' | 'kitchen' | 'delivery' | 'waiter';
    pin: string;
    location?: { lat: number; lng: number; accuracy: number };
  }): Promise<AuthUser>;
  waiterLogin(username: string, password: string): Promise<AuthUser>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const USER_KEY = 'menucloud.user';
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(USER_KEY) || 'null') as AuthUser | null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const persist = useCallback((next: AuthUser | null, token?: string | null) => {
    setUser(next);
    if (next) sessionStorage.setItem(USER_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(USER_KEY);
    if (token !== undefined) storeToken(token);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await apiRequest<{ user: AuthUser }>('/api/auth/me');
      persist(data.user);
    } catch {
      persist(null, null);
    }
  }, [persist]);

  useEffect(() => {
    if (!getStoredToken()) {
      persist(null);
      setLoading(false);
      return;
    }
    void refresh().finally(() => setLoading(false));
  }, [persist, refresh]);

  const login = useCallback(async (email: string, password: string) => {
    queryClient.clear();
    const data = await apiRequest<{ token: string; user: AuthUser }>('/api/auth/login', {
      method: 'POST',
      auth: false,
      tenant: false,
      body: jsonBody({ email, password }),
    });
    persist(data.user, data.token);
    return data.user;
  }, [persist]);

  const employeeLogin = useCallback(async (input: {
    branchId: string;
    role: 'reception' | 'kitchen' | 'delivery' | 'waiter';
    pin: string;
    location?: { lat: number; lng: number; accuracy: number };
  }) => {
    queryClient.clear();
    const data = await apiRequest<{ token: string; user: AuthUser }>('/api/auth/employee-pin', {
      method: 'POST',
      auth: false,
      body: jsonBody(input),
    });
    persist(data.user, data.token);
    return data.user;
  }, [persist]);

  const waiterLogin = useCallback(async (username: string, password: string) => {
    queryClient.clear();
    const data = await apiRequest<{ token: string; user: AuthUser }>('/api/auth/waiter-login', {
      method: 'POST',
      auth: false,
      body: jsonBody({ username, password }),
    });
    persist(data.user, data.token);
    return data.user;
  }, [persist]);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } finally {
      queryClient.clear();
      persist(null, null);
    }
  }, [persist]);

  const value = useMemo(() => ({ user, loading, login, employeeLogin, waiterLogin, logout, refresh }), [user, loading, login, employeeLogin, waiterLogin, logout, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth debe usarse dentro de AuthProvider.');
  return value;
}
